import { useState, useEffect, useMemo, useRef } from 'react'
import { MdSave, MdCancel, MdAdd, MdDelete } from 'react-icons/md'
import { Button, LoadingSpinner } from '../../components/ui'
import { useAirport } from '../../hooks/useAirport'

function AirportForm({ airport, onSave, onCancel, mode = 'create' }) {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    city: '',
    country: '',
    state: '',
    stateCode: '',
    latitude: '',
    longitude: '',
    timezone: '',
    elevation: '',
    type: 'domestic',
    isActive: true,
    runways: [],
  })
  const [errors, setErrors] = useState({})
  const hasPopulatedForm = useRef(false)

  const { createAirportAsync, updateAirportAsync, isCreateAirportLoading, isUpdateAirportLoading } =
    useAirport()

  // Create a stable reference for the airport object
  const runwaysString = JSON.stringify(airport?.runways)
  const stableAirport = useMemo(
    () => airport,
    [ // eslint-disable-line react-hooks/exhaustive-deps
      airport?._id,
      airport?.name,
      airport?.code,
      airport?.city,
      airport?.country,
      airport?.state,
      airport?.stateCode,
      airport?.latitude,
      airport?.longitude,
      airport?.timezone,
      airport?.elevation,
      airport?.type,
      airport?.isActive,
      runwaysString,
    ],
  )

  useEffect(() => {
    if (stableAirport && mode === 'edit' && !hasPopulatedForm.current) {
      setFormData({
        name: stableAirport.name || '',
        code: stableAirport.code || '',
        city: stableAirport.city || '',
        country: stableAirport.country || '',
        state: stableAirport.state || '',
        stateCode: stableAirport.stateCode || '',
        latitude: stableAirport.latitude || '',
        longitude: stableAirport.longitude || '',
        timezone: stableAirport.timezone || '',
        elevation: stableAirport.elevation || '',
        type: stableAirport.type || 'domestic',
        isActive: stableAirport.isActive !== undefined ? stableAirport.isActive : true,
        runways: stableAirport.runways || [],
      })
      hasPopulatedForm.current = true
    }
  }, [stableAirport, mode])

  // Reset the flag when switching between create and edit modes
  useEffect(() => {
    hasPopulatedForm.current = false
  }, [mode])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Airport name is required'
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Airport code is required'
    } else if (formData.code.length !== 3) {
      newErrors.code = 'Airport code must be 3 characters'
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required'
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required'
    }

    if (
      formData.latitude &&
      (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90)
    ) {
      newErrors.latitude = 'Latitude must be between -90 and 90'
    }

    if (
      formData.longitude &&
      (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180)
    ) {
      newErrors.longitude = 'Longitude must be between -180 and 180'
    }

    if (formData.elevation && isNaN(formData.elevation)) {
      newErrors.elevation = 'Elevation must be a number'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleRunwayChange = (index, field, value) => {
    const updatedRunways = [...formData.runways]
    updatedRunways[index] = {
      ...updatedRunways[index],
      [field]: value,
    }
    setFormData((prev) => ({
      ...prev,
      runways: updatedRunways,
    }))
  }

  const addRunway = () => {
    setFormData((prev) => ({
      ...prev,
      runways: [
        ...prev.runways,
        {
          number: '',
          length: '',
          width: '',
          surface: 'asphalt',
        },
      ],
    }))
  }

  const removeRunway = (index) => {
    setFormData((prev) => ({
      ...prev,
      runways: prev.runways.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const airportData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        elevation: formData.elevation ? parseInt(formData.elevation) : null,
        runways: formData.runways.filter(
          (runway) => runway.number && runway.length && runway.width,
        ),
      }

      let result
      if (mode === 'create') {
        result = await createAirportAsync(airportData)
      } else {
        result = await updateAirportAsync(airport._id, airportData)
      }

      if (onSave) {
        onSave(result)
      }
    } catch (error) {
      console.error('Error saving airport:', error)
    }
  }

  const isLoading = isCreateAirportLoading || isUpdateAirportLoading

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Basic Information
          </h3>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Airport Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white ${
                errors.name ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="Salt Lake City International Airport"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Airport Code *
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              maxLength={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white uppercase ${
                errors.code ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="SLC"
            />
            {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Type
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
            >
              <option value="domestic">Domestic</option>
              <option value="international">International</option>
              <option value="regional">Regional</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
              Active Airport
            </label>
          </div>
        </div>

        {/* Location Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Location</h3>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              City *
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white ${
                errors.city ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="Salt Lake City"
            />
            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                State/Province
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                placeholder="Utah"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                State Code
              </label>
              <input
                type="text"
                name="stateCode"
                value={formData.stateCode}
                onChange={handleInputChange}
                maxLength={2}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white uppercase"
                placeholder="UT"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Country *
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white ${
                errors.country ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="United States"
            />
            {errors.country && <p className="mt-1 text-sm text-red-600">{errors.country}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Latitude
              </label>
              <input
                type="number"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                step="any"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white ${
                  errors.latitude ? 'border-red-500' : 'border-neutral-300'
                }`}
                placeholder="40.78886"
              />
              {errors.latitude && <p className="mt-1 text-sm text-red-600">{errors.latitude}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Longitude
              </label>
              <input
                type="number"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                step="any"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white ${
                  errors.longitude ? 'border-red-500' : 'border-neutral-300'
                }`}
                placeholder="-111.979866"
              />
              {errors.longitude && <p className="mt-1 text-sm text-red-600">{errors.longitude}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Elevation (ft)
              </label>
              <input
                type="number"
                name="elevation"
                value={formData.elevation}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white ${
                  errors.elevation ? 'border-red-500' : 'border-neutral-300'
                }`}
                placeholder="4227"
              />
              {errors.elevation && <p className="mt-1 text-sm text-red-600">{errors.elevation}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Timezone
              </label>
              <input
                type="text"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                placeholder="America/Denver"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Runways Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Runways</h3>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addRunway}
            className="flex items-center space-x-2"
          >
            <MdAdd className="text-lg" />
            <span>Add Runway</span>
          </Button>
        </div>

        {formData.runways.length === 0 ? (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            No runways added yet. Click "Add Runway" to add runway information.
          </p>
        ) : (
          <div className="space-y-4">
            {formData.runways.map((runway, index) => (
              <div
                key={index}
                className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-neutral-900 dark:text-white">
                    Runway {index + 1}
                  </h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRunway(index)}
                    className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <MdDelete className="text-lg" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Number
                    </label>
                    <input
                      type="text"
                      value={runway.number || ''}
                      onChange={(e) => handleRunwayChange(index, 'number', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                      placeholder="01/19"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Length (m)
                    </label>
                    <input
                      type="number"
                      value={runway.length || ''}
                      onChange={(e) => handleRunwayChange(index, 'length', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                      placeholder="3000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Width (m)
                    </label>
                    <input
                      type="number"
                      value={runway.width || ''}
                      onChange={(e) => handleRunwayChange(index, 'width', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                      placeholder="45"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Surface
                    </label>
                    <select
                      value={runway.surface || 'asphalt'}
                      onChange={(e) => handleRunwayChange(index, 'surface', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                    >
                      <option value="asphalt">Asphalt</option>
                      <option value="concrete">Concrete</option>
                      <option value="grass">Grass</option>
                      <option value="dirt">Dirt</option>
                      <option value="gravel">Gravel</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-neutral-200 dark:border-neutral-700">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          <MdCancel className="text-lg mr-2" />
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? (
            <LoadingSpinner size="sm" className="mr-2" />
          ) : (
            <MdSave className="text-lg mr-2" />
          )}
          {mode === 'create' ? 'Create Airport' : 'Update Airport'}
        </Button>
      </div>
    </form>
  )
}

export default AirportForm
